import { Select, Chevron } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { DashboardFilters } from 'screens/Reports/MonitoringReports'
import styled from 'styled-components'
import { FilterValues } from '../hooks/useIncidentsReportList'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import useResidents from 'hooks/useFindItem/useResidents'
import { getName } from 'screens/Todos/components/BasicFilters'
import {
  Home,
  IncidentSeverityValues,
  IncidentStatus,
  LogCategoryItem,
} from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { capitalize } from 'lodash'
import { getSeverityOptions } from 'screens/Logs/LogScreen/components/IncidentLogScreens/utils/incidentSeverityUtils'
import { Filter } from 'screens/Reports/MonitoringReports/components/DashboardFilters/SelectedFilters'
import { getNominative } from 'common/utils'
import { SetFilterParams } from '../screens/IncidentsDashboard/IncidentsDashboard'

export type IncidentFiltersProps = {
  filterParams: FilterValues
  setFilterParams: SetFilterParams
  hasResidentsFilter?: boolean
  hasHomesFilter?: boolean
  hasOnClick?: boolean
}

const INCIDENT_CATEGORY_ITEM = '5e867702b349f776c8d48c34'

export const PlaceHolder = (text: string) => (
  <StyledPlaceholder>{text}</StyledPlaceholder>
)

export const IncidentFilters = ({
  filterParams,
  setFilterParams,
  hasResidentsFilter,
  hasHomesFilter,
}: IncidentFiltersProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const value = filterParams
  const { all: allResidents } = useResidents({})
  const {
    'log-category-items': categoryItems,
  }: {
    'log-category-items': LogCategoryItem[] | undefined
  } = useSelector(
    (state: { data: { 'log-category-items': LogCategoryItem[] } }) => state.data
  )

  const home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const homes = useSelector(
    (state: { data: { homes: Home[] } }) => state.data.homes
  )

  const preferredPeopleNominative: string = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative || ''
  )

  const severities = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.incidentSeverity || ''
  )

  const locationsOptions = home?.locations?.map((location) => ({
    value: location,
    label: location,
  }))

  const homeOptions = homes?.map((home) => ({
    value: home.id,
    label: home.name,
  }))

  const resultedInOptions = [
    {
      value: 'chemicalRestraints',
      label: t(incidentReportsConstants.CHEMICAL_RESTRAINTS_APPLIED),
    },
    {
      value: 'damages',
      label: t(incidentReportsConstants.DAMAGES),
    },
    {
      value: 'carersInjuries',
      label: t(incidentReportsConstants.INJURIES_OF_CARERS),
    },
    {
      value: 'residentInjuries',
      label: t(incidentReportsConstants.INJURIES_OF_RESIDENT, {
        variable: getNominative(preferredPeopleNominative).toLowerCase(),
      }),
    },
    {
      value: 'otherPartiesInjuries',
      label: t(incidentReportsConstants.INJURIES_OF_OTHER_PARTIES),
    },

    {
      value: 'physicalIntervention',
      label: t(incidentReportsConstants.PHYSICAL_INTERVENTION_APPLIED),
    },
    {
      value: 'prnMedication',
      label: t(incidentReportsConstants.PRN_MEDICATIONS),
    },
    {
      value: 'restraints',
      label: t(incidentReportsConstants.RESTRAINTS_APPLIED),
    },
  ].map((status) => ({
    value: status.value,
    label: status.label,
  }))

  const statusOptions = [
    {
      value: IncidentStatus.closed,
      label: capitalize(t(incidentReportsConstants[IncidentStatus.closed])),
    },
    {
      value: IncidentStatus.needsReview,
      label: capitalize(
        t(incidentReportsConstants[IncidentStatus.needsReview])
      ),
    },
    {
      value: IncidentStatus.open,
      label: capitalize(t(incidentReportsConstants[IncidentStatus.open])),
    },
  ].map((status) => ({
    value: status.value,
    label: status.label,
  }))

  const severitiesOptions = getSeverityOptions(
    severities as IncidentSeverityValues,
    t
  )?.map((severity) => ({
    value: severity.value.toString(),
    label: severity.label,
  }))

  const mappedSelectedFilters = {
    ...value,
    ...(value.homes) && homes && {
      homes: value.homes.map((homeId) => {
        return homes.find((h) => h.id === homeId)!.name
      }),
    },
    ...(value.residents && {
      residents: value.residents.map((resident) => {
        const residentName = allResidents?.find((r) => r.id === resident)
        return residentName ? getName(residentName) : null
      }),
    }),
    ...(value.categoryItems && {
      categoryItems: value.categoryItems.map(
        (category) => categoryItems?.find((c) => c.id === category)?.name
      ),
    }),
    ...(value.statuses && {
      statuses: value.statuses.map(
        (status) =>
          statusOptions?.find((s) => s.value.toString() === status)?.label
      ),
    }),
    ...(value.severities && {
      severities: value.severities.map(
        (severity) =>
          severitiesOptions?.find((s) => s.value.toString() === severity)?.label
      ),
    }),
    ...(value.resultedIn && {
      resultedIn: value.resultedIn.map(
        (result) => resultedInOptions?.find((r) => r.value === result)?.label
      ),
    }),
  }

  const onFilterChange = (value: FilterValues) => {
    const newValue = { ...value }
    setFilterParams((prevFilters: FilterValues) => ({
      ...prevFilters,
      ...newValue,
    }))
  }

  return (
    <DashboardFilters
      onChange={onFilterChange}
      value={filterParams}
      withLoggedBy={false}
      reportingId="items"
      itemsPlaceholder={t(incidentReportsConstants.INCIDENT_TITLE)}
      itemsCategoryFilter={INCIDENT_CATEGORY_ITEM}
      hasResidentsFilter={hasResidentsFilter}
      allowClearDateRange={false}
      variant="highContrast"
      selectedFilters={mappedSelectedFilters as Filter}
      //   ref={filtersRef}
    >
      {hasHomesFilter && (
        <StyledSelect
          placeholder={PlaceHolder(t(incidentReportsConstants.SITES))}
          optionRender={(option) => <Text>{option.label}</Text>}
          options={homeOptions}
          value={filterParams.homes}
          onChange={(selectedHomes) =>
            onFilterChange({ homes: selectedHomes } as FilterValues)
          }
          mode="multiple"
        />
      )}
      {home?.features?.hasHomeLocations?.enabled && (
        <StyledSelect
          placeholder={PlaceHolder(t(incidentReportsConstants.LOCATION))}
          optionRender={(option) => <Text>{option.label}</Text>}
          options={locationsOptions}
          value={filterParams.locations}
          onChange={(selectedLocations) =>
            onFilterChange({ locations: selectedLocations } as FilterValues)
          }
          mode="multiple"
          suffixIcon={<Chevron direction="down" />}
        />
      )}
      <StyledSelect
        placeholder={PlaceHolder(t(incidentReportsConstants.STATUS))}
        optionRender={(option) => <Text>{option.label}</Text>}
        options={statusOptions}
        value={filterParams.statuses}
        onChange={(selectedStatus) =>
          onFilterChange({ statuses: selectedStatus } as FilterValues)
        }
        mode="multiple"
        suffixIcon={<Chevron direction="down" />}
      />
      <StyledSelect
        placeholder={PlaceHolder(t(incidentReportsConstants.SEVERITIES))}
        options={severitiesOptions}
        value={filterParams.severities}
        onChange={(selectedSeverities) =>
          onFilterChange({ severities: selectedSeverities } as FilterValues)
        }
        mode="multiple"
        suffixIcon={<Chevron direction="down" />}
      />
      <StyledSelect
        placeholder={PlaceHolder(t(incidentReportsConstants.RESULTED_IN))}
        optionRender={(option) => <Text>{option.label}</Text>}
        options={resultedInOptions}
        value={filterParams.resultedIn}
        onChange={(selectedResulted) =>
          onFilterChange({ resultedIn: selectedResulted } as FilterValues)
        }
        mode="multiple"
        suffixIcon={<Chevron direction="down" />}
      />
    </DashboardFilters>
  )
}

const StyledSelect = styled(Select)`
  min-width: 200px;

  .ant-select-selector {
    border-color: ${({ theme }) => theme.color.neutral.color5} !important;
  }

  .ant-select-arrow {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }

  ${({ theme }) => theme.mediaQueries.mobileDown} {
    width: 100%;
  }
` as typeof Select

const StyledPlaceholder = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color3};
`
